import { NotificationAction } from '@/types/notification.types';
import { NavigateFunction } from 'react-router-dom';

/**
 * Service per interpretare ed eseguire le azioni delle notifiche
 * 
 * Supporta:
 * - redirect: naviga verso un path con parametri opzionali
 * - execute_function: (futuro) esegue una funzione registrata
 * - open_modal: (futuro) apre un modal specifico
 */
export class NotificationActionService {
  private navigate: NavigateFunction;

  constructor(navigate: NavigateFunction) {
    this.navigate = navigate;
  }

  /**
   * Esegue l'azione specificata nella notifica
   */
  executeAction(action?: NotificationAction): void {
    if (!action) {
      console.warn('No action defined for notification');
      return;
    }

    switch (action.type) {
      case 'redirect':
        this.handleRedirect(action);
        break;
      case 'execute_function':
        this.handleExecuteFunction(action);
        break;
      case 'open_modal':
        this.handleOpenModal(action);
        break;
      default:
        console.warn(`Unknown action type: ${action.type}`);
    }
  }

  /**
   * Gestisce il redirect costruendo l'URL dai parametri
   */
  private handleRedirect(action: NotificationAction): void {
    const { path, params } = action.payload;

    if (!path) {
      console.error('Redirect action requires a path');
      return;
    }

    // Costruisce l'URL con i parametri query
    let url = path;
    
    if (params && Object.keys(params).length > 0) {
      const searchParams = new URLSearchParams(params);
      url = `${path}?${searchParams.toString()}`;
    }

    console.log('Navigating to:', url);
    this.navigate(url);
  }

  /**
   * Gestisce l'esecuzione di funzioni (futuro)
   */
  private handleExecuteFunction(action: NotificationAction): void {
    const { functionName } = action.payload;

    if (!functionName) {
      console.error('execute_function action requires a functionName');
      return;
    }

    // TODO: Implementare registro di funzioni eseguibili
    console.warn(`Function execution not yet implemented: ${functionName}`);
    
    // Esempio futuro:
    // const functionRegistry = {
    //   'refreshDashboard': () => { /* ... */ },
    //   'showQuickAction': (params) => { /* ... */ },
    // };
    // 
    // if (functionRegistry[functionName]) {
    //   functionRegistry[functionName](action.payload);
    // }
  }

  /**
   * Gestisce l'apertura di modal (futuro)
   */
  private handleOpenModal(action: NotificationAction): void {
    const { modalId } = action.payload;

    if (!modalId) {
      console.error('open_modal action requires a modalId');
      return;
    }

    // TODO: Implementare sistema di modal registry
    console.warn(`Modal opening not yet implemented: ${modalId}`);
    
    // Esempio futuro:
    // const modalRegistry = {
    //   'itineraryDetails': (params) => { /* ... */ },
    //   'userProfile': (params) => { /* ... */ },
    // };
    // 
    // if (modalRegistry[modalId]) {
    //   modalRegistry[modalId](action.payload);
    // }
  }

  /**
   * Factory method statico per creare un'istanza del service
   */
  static create(navigate: NavigateFunction): NotificationActionService {
    return new NotificationActionService(navigate);
  }
}

// =====================================================
// ESEMPI DI UTILIZZO
// =====================================================

/**
 * Esempio 1: Redirect alla dashboard con tab specifico
 * 
 * action: {
 *   type: 'redirect',
 *   payload: {
 *     path: '/dashboard',
 *     params: { tab: 'pending' }
 *   }
 * }
 * 
 * Risultato: navigate('/dashboard?tab=pending')
 */

/**
 * Esempio 2: Redirect al builder con itinerario e step
 * 
 * action: {
 *   type: 'redirect',
 *   payload: {
 *     path: '/builder',
 *     params: {
 *       itineraryId: 'uuid-123',
 *       step: '2'
 *     }
 *   }
 * }
 * 
 * Risultato: navigate('/builder?itineraryId=uuid-123&step=2')
 */

/**
 * Esempio 3: Redirect al dettaglio travel request
 * 
 * action: {
 *   type: 'redirect',
 *   payload: {
 *     path: '/travel-requests/detail',
 *     params: { id: 'uuid-456' }
 *   }
 * }
 * 
 * Risultato: navigate('/travel-requests/detail?id=uuid-456')
 */
